import { useState } from "react";

import { AxiosError } from "axios";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import useAxiosPrivate from "../../../api/hooks/useAxiosPrivate";
import { ALERT_STATUS } from "../../../context/alert/types";
import { Filter, ROUTES } from "../../../shared";
import useResponse from "../../../shared/hooks/useResponse";
import { FILTER_NAMES, FilterChangePropsType } from "../types";

const useFilterDropdown = (route: ROUTES, filterName?: FILTER_NAMES) => {
  const { getData } = useAxiosPrivate();
  const { handleResponse } = useResponse();

  const { setParams, fetchData } = useTableStoreByRoute(route);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<Filter[]>([]);

  const resource = `${route}/Filters?filter=${filterName}`;

  const getFilterOptions = async () => {
    try {
      setIsLoading(true);
      await getData(resource).then(response => {
        setOptions(response.data);
      });
    } catch (error) {
      const err = error as AxiosError<any>;
      if (err) {
        const responseMessage = err.response?.data.message;
        handleResponse(ALERT_STATUS.Critical, responseMessage || err?.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChangeValue = ({
    isTableLoading,
    filterObject,
    newValue,
    filterName,
    setSelectedItem,
    setValue
  }: FilterChangePropsType) => {
    if (!isTableLoading) {
      let currentValue = filterObject ?? {};

      // Remove possible undefined/null entries (e.g. MUI Autocomplete can emit
      // an undefined value when a highlighted option becomes stale mid-typing)
      const sanitizedNewValue = newValue.filter(Boolean);

      const selectedValue = sanitizedNewValue.filter(
        (x: Filter) => !currentValue[filterName]?.includes(x)
      );
      const unselectedValue = currentValue[filterName]?.filter(
        (x: Filter) => !sanitizedNewValue?.includes(x)
      );

      if (setSelectedItem) {
        selectedValue.length > 0
          ? setSelectedItem(selectedValue[0])
          : setSelectedItem(unselectedValue?.[0] ?? null);
      }

      const mergedFiltersParams = {
        ...currentValue,
        [filterName]: sanitizedNewValue
      };

      // Update filters in table store
      setParams({
        filters: mergedFiltersParams,
        pageNumber: "1" // Reset to first page when filter changes
      });

      setValue(sanitizedNewValue);
      fetchData(getData, handleResponse);
    }
  };

  return {
    isOpen,
    isLoading,
    options,
    setIsOpen,
    getFilterOptions,
    handleFilterChangeValue
  };
};

export default useFilterDropdown;
